import Bugsnag from '@nextories/vue-core-components/src/utils/modules/bugsnag';
import fieldUtils from '@nextories/vue-core-components/src/utils/helpers/fieldUtils';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { i18n } from "@/plugins/i18n";
import { customFieldsActions, estimateActions, meetingActions } from '@nextories/vue-core-components/src/store/common';
import type {Field, FormConfig, Step} from '@nextories/vue-core-components/src/types/FormConfig';
import { useCustomFieldsStore } from '@nextories/vue-core-components/src/store/customFieldsStore';
import { useMeetingStore } from '@nextories/vue-core-components/src/store/meetingStore';
import { useEstimateStore } from '@nextories/vue-core-components/src/store/estimateStore';
import { useAnalyticsStore } from '@nextories/vue-core-components/src/store/analyticsStore';
import { cloneWithoutReactivity } from '@nextories/vue-core-components/src/store/common';
import type { ProjectRequest } from '@/types/ProjectRequest';

type StrInt = string | number;

function removeItem(items: { [key: string]: string }[], key: string, value: string) {
  const itemFindIndex = items.findIndex((item) => item[key] === value);
  if (itemFindIndex !== -1) {
    items.splice(itemFindIndex, 1);
  }
  return items;
}

// Send options parameters (gclid, msclkid, utmSource, utmCampaign, utmMedium)
// to "form data"
// or send parameters (partnerId, partnerLeadId) to "param"
function setParamRequest(request: ProjectRequest, state: { [key: string]: string }) {
  const param: { [key: string]: string } = {};
  if (state.partnerId) {
    param.partner = state.partnerId;
  }
  const other: { [key: string]: string } = {};
  const items: (string | { value: string, notFormData?: boolean, dataIsPriority?: boolean })[] = ['gclid', 'msclkid',
    {
      value: 'partnerLeadId',
      notFormData: true
    },
    {
      value: 'utmSource',
      dataIsPriority: true
    },
    {
      value: 'utmCampaign',
      dataIsPriority: true
    },
    {
      value: 'utmMedium',
      dataIsPriority: true
    }];
  for (const item
    of items) {
    const value = typeof item === 'string' ? item : item.value;
    const key = String(value.replace(/([A-Z])/g, '_$1').toLowerCase());
    if (state[value]) {
      if (typeof item === 'object' && item.notFormData) {
        param[key] = state[value];
      } else {
        other[key] = typeof item === 'object' && item.dataIsPriority && request.formData?.[key]
          ? request.formData[key] : state[value];
      }
    }
  }
  Object.assign(request, param);
  if (Object.entries(other).length > 0) {
    Object.assign(request.formData, other);
  }
  return request;
}

export const usePartnerStore = defineStore('partner', {
 state: () => {
   return {
     ...cloneWithoutReactivity(useCustomFieldsStore().$state),
     ...cloneWithoutReactivity(useEstimateStore().$state),
     ...cloneWithoutReactivity(useMeetingStore().$state),
     crmToken: '',
     onlineBookingLoading: false,
     partnerLoading: false,
     partnerError: false,
     partnerStyle: {},
     onlineBooking: true,
     minarmRights: null,
     partnerLeadId: '',
     utmSource: '',
     utmMedium: '',
     utmCampaign: '',
     gclid: '',
     msclkid: '',
     partnerId: '',
     configId: '',
     customId: '',
   };
 },
  actions: {
    ...customFieldsActions,
    ...estimateActions,
    ...meetingActions,
    track() {
      if (this.currentStep?.data_analytics) {
        useAnalyticsStore().track({
          dimensions: {
            dimension1: this.partnerId,
            dimension2: this.allowEstimate,
            dimension3: this.estimationDone,
            dimension5: this.utmSource,
            dimension6: this.utmMedium,
            dimension7: this.utmCampaign,
            user_properties: {
              PARTNER_ID: this.partnerId,
              ALLOW_ESTIMATION: this.allowEstimate,
              ESTIMATION_DONE: this.estimationDone,
              NXT_SOURCE: this.utmSource,
              NXT_MEDIUM: this.utmMedium,
              NXT_CAMPAIGN: this.utmCampaign,
            },
          },
          category: import.meta.env.nxt.analytics.category,
          label: import.meta.env.nxt.analytics
            .label[this.currentStep.data_analytics],
          action: this.currentStep.data_analytics,
        });
      }
    },
    getPartner(params: { partnerId: string, configId: string }) {
      this.partnerLoading = true;
      return window.connector.getPartner(params.partnerId, params.configId)
        .then(async (result: { config: FormConfig, style: Record<string, string> }) => {
          this.setPartnerConfig(result);
          this.partnerId = params.partnerId;
          this.configId = params.configId;
        })
        .catch((error: string) => {
          this.partnerError = true;
          throw new Error(error);
        })
        .finally(() => {
          this.partnerLoading = false;
        });
    },
    getOnlineBooking() {
      this.onlineBookingLoading = true;
      return window.connector
        .getOnlineBooking()
        .then(async (result: { online_booking_url: string }) => {
          window.location = result.online_booking_url;
        })
        .catch((error: string) => {
          this.messageError = i18n.global.t('base.messageError.onlineBooking');
          throw new Error(error);
        })
        .finally(() => {
          this.onlineBookingLoading = false;
        });
    },
    postProject(request: ProjectRequest) {
      this.setDataSaving(true);
      return window.connector.postProject(setParamRequest(request, this))
        .then((project: {[key: string]: string | number | boolean | {} | [], token: string, crm_token: string }) => {
          window.connector.setProjectToken(project.token);
          window.connector.setCrmToken(project.crm_token);
          this.setProject(project);
          this.dataError = false;
          useEstimateStore().setEstimate();
          this.meetTypeAvailable = '';
        })
        .catch((error: string | { error: string }) => {
          if (typeof error === 'object' && error.error === 'Could not calculate distance.') {
            this.messageError = i18n.global.t('base.messageError.calculateDistance');
          } else {
            this.messageError = i18n.global.t('base.messageError.saveProject');
          }
          this.dataError = true;
          throw new Error(typeof error === 'string' ? error : 'error' in error ? error.error : 'Unknown error');
        })
        .finally(() => {
          this.setDataSaving(false);
        });
    },
    updateProject(request: ProjectRequest) {
      this.setDataSaving(true);
      return window.connector.updateProject(setParamRequest(request, this))
        .then((project: {[key: string]: string | number | boolean | {} | [], token: string, crm_token: string }) => {
          this.setProject(project);
          this.dataError = false;
          useEstimateStore().setEstimate();
          this.meetTypeAvailable = '';
        })
        .catch((error: string | { error: string }) => {
          if (typeof error === 'object' && error.error === 'Could not calculate distance.') {
            this.messageError = i18n.global.t('base.messageError.calculateDistance');
          } else {
            this.messageError = i18n.global.t('base.messageError.saveProject');
          }
          this.dataError = true;
          throw new Error(typeof error === 'string' ? error : 'error' in error ? error.error : 'Unknown error');
        })
        .finally(() => {
          this.setDataSaving(false);
        });
    },
    postCalcMinarmRights(request: { formData: Record<string, string> }) {
      this.setDataSaving(true);
      return window.connector.postCalcMinarmRights(request)
        .then((result: { verify_plafond_vol: boolean | string | number | {}, verify_plafond_price: boolean | string | number | {}  }) => {
          this.minarmRights = {
            verify_plafond_vol: result.verify_plafond_vol,
            verify_plafond_price: result.verify_plafond_price,
          };
          this.dataError = false;
        })
        .catch((error: string | { error: string }) => {
          this.dataError = true;
          this.messageError = i18n.global.t('base.messageError.minarmRights');
          throw new Error(typeof error === 'string' ? error : 'error' in error ? error.error : 'Unknown error');
        })
        .finally(() => {
          this.setDataSaving(false);
        });
    },
    setPartnerLeadId(id?: string | number) {
      if (id) {
        this.partnerLeadId = id;
      }
    },
    setPartnerConfig(result: {
      config: FormConfig,
      style: string,
    }) {
      useAnalyticsStore().setGaDeactivate(!!result.config.ga_deactivate || useAnalyticsStore().gaDeactivate);
      useAnalyticsStore().iniGtag();
      if (!result.config.disable_error_reporting) {
        Bugsnag.config(import.meta.env.nxt);
      }
      this.partnerStyle = result.style;
      this.steps = [...result.config.steps];
      if (this.initialData && Object.keys(this.initialData).length > 0) {
        let errorField = false;

        for (const [index, step] of Object.entries(this.steps as Step[])) {
          for (const [i, field] of Object.entries(step.fields as unknown as Record<string, Field>)) {
            for (const data of Object.entries(this.initialData)) {
              if (field.id === data[0]) {
                // eslint-disable-next-line prefer-destructuring
                field.value = data[1];
                // SAVE INITIAL VALUE
                if (data[1] !== undefined) {
                  // eslint-disable-next-line prefer-destructuring
                  field.initialValue = data[1];
                }
              }
            }

            if (!errorField && step.force_next_step) {
              errorField = !!fieldUtils.isErrorField(field);
              // eslint-disable-next-line radix
              this.currentStepNb = Number.parseInt(index);
              if (!errorField) {
                field.visibility = true;
                // eslint-disable-next-line radix
                if (Number.parseInt(i) + 1 === step.fields.length) {
                  this.track();
                }
                this.currentStepNb += 1;
              }
            }
          }
        }
        if (this.steps?.[0]?.action === 'check_allow_estimate'
          && this.steps?.[0]?.fields && this.currentStepNb > 0) {
          this.postAllowEstimate(fieldUtils.cleanData([this.steps[0]]));
          this.currentStepNb -= 1;
        }
      }
      [
        {
          id: 'scrollYAuto',
          name: 'scroll_y_auto'
        },
        {
          id: 'fixedHeaderHeight',
          name: 'fixed_header_height'
        },
        {
          id: 'onlineBooking',
          name: 'online_booking'
        }].forEach((setting) => {
        this[setting.id] = result.config[setting.name];
      });
      this.totalSteps = this.steps.length;
      this.currentStep = this.steps[this.currentStepNb];
      this.currentStep.readyForAutoScroll = true;
      if (result.config?.header) {
        this.headerConfig = result.config.header;
      }
      this.track();
    },
    setPartnerId(partnerId?: StrInt) {
      this.partnerId = partnerId;
    },
    setConfigId(configId?: StrInt) {
      this.configId = configId;
    },
    setUtmSource(utm?: StrInt) {
      this.utmSource = utm;
    },
    setUtmMedium(utm?: StrInt) {
      this.utmMedium = utm;
    },
    setUtmCampaign(utm?: StrInt) {
      this.utmCampaign = utm;
    },
    setGclid(gclid?: StrInt) {
      this.gclid = gclid;
    },
    setMsclkid(msclkid?: StrInt) {
      this.msclkid = msclkid;
    },
    setProject(result: {
      token: string,
      crm_token: string,
    }) {
      this.projectToken = result.token;
      this.crmToken = result.crm_token;
      if (!this.onlineBooking && this.meetTypeAvailable?.length) {
        removeItem(this.meetTypeAvailable, 'value', 'ONLINE');
      }
    },
    setCustomId(value?: StrInt) {
      this.customId = value;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePartnerStore, import.meta.hot))
}
